import React, { useState, useEffect, createContext, useContext, useMemo, useCallback, Suspense } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { message } from 'antd';

import apiService from './services/api.service';
import { setActiveTenant } from './components/layout/TenantSwitcher';

async function bootstrapTenantContext(user) {
    if (!user) return;
    const isSuper = user.role === 'super_admin' || user.role === 'super-admin';
    const homeId = user.homeTenantId ?? user.tenantId;
    const stored = localStorage.getItem('upos_active_tenant_id');

    // Regular users must always load their own business (ignore stale QA/super-admin tenant IDs).
    if (!isSuper) {
        if (homeId) {
            setActiveTenant(homeId, user.businessName);
        }
        return;
    }

    // Super admin: reuse stored tenant only when it still exists and is active.
    if (stored) {
        try {
            const result = await apiService.tenant.getAll();
            const tenants = result.success ? (result.tenants || []) : [];
            const match = tenants.find((t) => String(t.id) === String(stored) && t.is_active !== false);
            if (match) {
                setActiveTenant(match.id, match.business_name);
                return;
            }
        } catch (err) {
            console.warn('Could not validate stored tenant:', err);
        }
    }

    try {
        const result = await apiService.tenant.getAll();
        if (result.success && result.tenants?.length) {
            const preferred = homeId
                ? result.tenants.find((t) => String(t.id) === String(homeId))
                : null;
            const pick = preferred
                || result.tenants.find((t) => t.is_active)
                || result.tenants[0];
            if (pick) setActiveTenant(pick.id, pick.business_name);
        }
    } catch (err) {
        console.error('Super admin tenant bootstrap failed:', err);
    }
}

import MainLayout from './components/layout/MainLayout';
import AccessDenied from './components/rbac/AccessDenied';
import { PageFallback, lazyWithRetry } from './routes/lazyPages';
import {
    DashboardPage, ProductsPage, CustomersPage, CustomerFormPage, CustomerDetailPage,
    CustomerPaymentFormPage, ProductFormPage, ProductCategoriesPage, UserFormPage,
    SalesPage, ReportsPage, SettingsPage, UsersPage, SuperAdminPage, ProfilePage,
    BankAccountsPage, BankAccountFormPage, BankPaymentsPage, BankPaymentFormPage,
    BankReceiptsPage, BankReceiptFormPage, BankTransfersPage, BankTransferFormPage,
    SaleDetailPage, SalesInvoicesPage, SalesInvoiceFormPage, SalesReturnsPage, SalesReturnFormPage, SalesReceiptsPage,
    SalesQuotationsPage, SalesQuotationFormPage, ExpenseIncomePage, PurchaseReturnsPage,
    PurchaseReturnFormPage, PurchaseReturnDetailPage,
    SalesReceiptFormPage, SalesReceiptDetailPage, TenantFormPage, SuppliersPage,
    SupplierFormPage, SupplierDetailPage, PurchaseBillsPage, PurchaseBillFormPage, PurchasePaymentsPage,
    PurchasePaymentFormPage, InventoryLocationsPage, InventoryLocationFormPage,
    StockAdjustmentsPage, StockAdjustmentFormPage, StockTransfersPage, StockTransferFormPage,
    InventoryBatchesPage, InventoryBatchFormPage, LowStockPage, InventoryUnitsPage,
    InventoryUnitFormPage, InventoryKitFormPage, ChartOfAccountsPage, ChartAccountFormPage,
    ChartOpeningBalancePage, JournalsPage, JournalFormPage, JournalDetailPage,
    FinancialReportsPage, FiscalYearPage, FiscalYearFormPage, TaxRatesPage, TaxRateFormPage,
    FbrPage, PrintTemplatesPage, PrintTemplateFormPage, EnterpriseAuditPage, CompanyAccessPage,
    CompanyAccessGrantPage, AccessScopesPage, ScheduledTasksPage, ScheduledTaskFormPage,
    ModulePlaceholderPage,
} from './routes/lazyPages';

const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'));

// Create Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="app-loader">
                <div className="spinner"></div>
                <div className="text">Loading...</div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Permission-gated Route Component
const PermissionedRoute = ({ permission, permissions, children }) => {
    const { user, loading, hasPermission, hasAnyPermission } = useAuth();

    if (loading) {
        return (
            <div className="app-loader">
                <div className="spinner"></div>
                <div className="text">Loading...</div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    const required = permissions || (permission ? [permission] : []);
    const allowed = required.length === 0 || hasAnyPermission(required);
    if (!allowed) {
        return <AccessDenied />;
    }

    return children;
};

// Super Admin only route
const SuperAdminRoute = ({ children }) => {
    const { user, loading, isSuperAdmin } = useAuth();

    if (loading) {
        return (
            <div className="app-loader">
                <div className="spinner"></div>
                <div className="text">Loading...</div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (!isSuperAdmin) {
        return <Navigate to="/" replace />;
    }

    return children;
};

function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const currentUser = await apiService.auth.getCurrentUser();
            setUser(currentUser);
            if (currentUser) {
                await bootstrapTenantContext(currentUser);
            }
        } catch (error) {
            console.error('Auth check error:', error);
        } finally {
            setLoading(false);
        }
    };

    const login = useCallback(async (username, password) => {
        try {
            const result = await apiService.auth.login(username, password);
            if (result.success) {
                setUser(result.user);
                await bootstrapTenantContext(result.user);
                message.success(`Welcome back, ${result.user.fullName}!`);
                return { success: true };
            }
            if (result.requiresOtp) {
                message.error('Two-factor login is disabled. Restart the API server (npm start) and try again.');
                return { success: false, error: 'Two-factor login is disabled. Restart the server and try again.' };
            }
            message.error(result.error || 'Login failed');
            return { success: false, error: result.error, locked: result.locked };
        } catch (error) {
            console.error('Login error:', error);
            const errMsg = error.response?.data?.error || 'Login failed';
            message.error(errMsg);
            return { success: false, error: errMsg };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await apiService.auth.logout();
            setUser(null);
            setActiveTenant(null);
            message.info('Logged out successfully');
        } catch (error) {
            console.error('Logout error:', error);
        }
    }, []);

    const permissions = user?.effectivePermissions || user?.permissions || [];

    const hasPermission = useCallback((permission) => {
        if (!user) return false;
        const perms = user.effectivePermissions || user.permissions || [];
        if (perms.includes('all')) return true;
        return perms.includes(permission);
    }, [user]);

    const hasAnyPermission = useCallback((permList) => {
        if (!user) return false;
        const perms = user.effectivePermissions || user.permissions || [];
        if (perms.includes('all')) return true;
        return permList.some((p) => perms.includes(p));
    }, [user]);

    const authValue = useMemo(() => ({
        user,
        loading,
        login,
        logout,
        isAdmin: user?.role === 'admin',
        isSuperAdmin: user?.role === 'super_admin' || user?.role === 'super-admin',
        permissions,
        hasPermission,
        hasAnyPermission,
    }), [user, loading, login, logout, permissions, hasPermission, hasAnyPermission]);

    return (
        <AuthContext.Provider value={authValue}>
            <Router>
                <Routes>
                    <Route path="/login" element={
                        user ? <Navigate to="/" replace /> : (
                            <Suspense fallback={<PageFallback />}>
                                <LoginPage />
                            </Suspense>
                        )
                    } />

                    <Route path="/" element={
                        <ProtectedRoute>
                            <MainLayout />
                        </ProtectedRoute>
                    }>
                        <Route index element={<PermissionedRoute permission="dashboard"><DashboardPage /></PermissionedRoute>} />
                        <Route path="products/new" element={<PermissionedRoute permission="products.create"><ProductFormPage /></PermissionedRoute>} />
                        <Route path="products/categories" element={<PermissionedRoute permission="products.categories"><ProductCategoriesPage /></PermissionedRoute>} />
                        <Route path="products/:id/edit" element={<PermissionedRoute permission="products.update"><ProductFormPage /></PermissionedRoute>} />
                        <Route path="products" element={<PermissionedRoute permission="products"><ProductsPage /></PermissionedRoute>} />
                        <Route path="inventory/locations/new" element={<PermissionedRoute permission="inventory.adjust"><InventoryLocationFormPage /></PermissionedRoute>} />
                        <Route path="inventory/locations" element={<PermissionedRoute permission="inventory"><InventoryLocationsPage /></PermissionedRoute>} />
                        <Route path="inventory/adjustments/new" element={<PermissionedRoute permission="inventory.adjust"><StockAdjustmentFormPage /></PermissionedRoute>} />
                        <Route path="inventory/adjustments" element={<PermissionedRoute permission="inventory.adjust"><StockAdjustmentsPage /></PermissionedRoute>} />
                        <Route path="inventory/transfers/new" element={<PermissionedRoute permission="inventory.adjust"><StockTransferFormPage /></PermissionedRoute>} />
                        <Route path="inventory/transfers" element={<PermissionedRoute permission="inventory.adjust"><StockTransfersPage /></PermissionedRoute>} />
                        <Route path="inventory/batches/new" element={<PermissionedRoute permission="products.batches"><InventoryBatchFormPage /></PermissionedRoute>} />
                        <Route path="inventory/batches" element={<PermissionedRoute permission="products.batches"><InventoryBatchesPage /></PermissionedRoute>} />
                        <Route path="inventory/low-stock" element={<PermissionedRoute permission="inventory"><LowStockPage /></PermissionedRoute>} />
                        <Route path="inventory/units/kits/new" element={<PermissionedRoute permission="products.update"><InventoryKitFormPage /></PermissionedRoute>} />
                        <Route path="inventory/units/new" element={<PermissionedRoute permission="products.update"><InventoryUnitFormPage /></PermissionedRoute>} />
                        <Route path="inventory/units" element={<PermissionedRoute permission="inventory"><InventoryUnitsPage /></PermissionedRoute>} />
                        <Route path="accounting/chart/new" element={<PermissionedRoute permission="accounting.chart"><ChartAccountFormPage /></PermissionedRoute>} />
                        <Route path="accounting/chart/:id/opening" element={<PermissionedRoute permission="accounting.journals"><ChartOpeningBalancePage /></PermissionedRoute>} />
                        <Route path="accounting/chart" element={<PermissionedRoute permission="accounting.chart"><ChartOfAccountsPage /></PermissionedRoute>} />
                        <Route path="accounting/journals/new" element={<PermissionedRoute permission="accounting.journals"><JournalFormPage /></PermissionedRoute>} />
                        <Route path="accounting/journals/edit/:draftId" element={<PermissionedRoute permission="accounting.journals"><JournalFormPage /></PermissionedRoute>} />
                        <Route path="accounting/journals/:id" element={<PermissionedRoute permission="accounting.journals"><JournalDetailPage /></PermissionedRoute>} />
                        <Route path="accounting/journals" element={<PermissionedRoute permission="accounting.journals"><JournalsPage /></PermissionedRoute>} />
                        <Route path="accounting/expense-income" element={<PermissionedRoute permission="accounting.journals"><ExpenseIncomePage /></PermissionedRoute>} />
                        <Route path="accounting/reports" element={<PermissionedRoute permission="accounting.reports"><FinancialReportsPage /></PermissionedRoute>} />
                        <Route path="accounting/fiscal/new" element={<PermissionedRoute permission="accounting.close"><FiscalYearFormPage /></PermissionedRoute>} />
                        <Route path="accounting/fiscal" element={<PermissionedRoute permission="accounting.close"><FiscalYearPage /></PermissionedRoute>} />
                        <Route path="tax/rates/new" element={<PermissionedRoute permission="fbr"><TaxRateFormPage /></PermissionedRoute>} />
                        <Route path="tax/rates" element={<PermissionedRoute permission="fbr"><TaxRatesPage /></PermissionedRoute>} />
                        <Route path="tax/fbr" element={<PermissionedRoute permission="fbr"><FbrPage /></PermissionedRoute>} />
                        <Route path="printing/templates/new" element={<PermissionedRoute permission="sales.print"><PrintTemplateFormPage /></PermissionedRoute>} />
                        <Route path="printing/templates/:id/edit" element={<PermissionedRoute permission="sales.print"><PrintTemplateFormPage /></PermissionedRoute>} />
                        <Route path="printing/templates" element={<PermissionedRoute permission="sales.print"><PrintTemplatesPage /></PermissionedRoute>} />
                        <Route path="enterprise/audit" element={<PermissionedRoute permission="audit"><EnterpriseAuditPage /></PermissionedRoute>} />
                        <Route path="enterprise/companies/grant" element={<PermissionedRoute permission="users.update"><CompanyAccessGrantPage /></PermissionedRoute>} />
                        <Route path="enterprise/companies" element={<PermissionedRoute permission="users"><CompanyAccessPage /></PermissionedRoute>} />
                        <Route path="enterprise/scopes" element={<PermissionedRoute permission="users.update"><AccessScopesPage /></PermissionedRoute>} />
                        <Route path="enterprise/tasks/new" element={<PermissionedRoute permission="settings.update"><ScheduledTaskFormPage /></PermissionedRoute>} />
                        <Route path="enterprise/tasks" element={<PermissionedRoute permission="settings.update"><ScheduledTasksPage /></PermissionedRoute>} />
                        <Route path="customers/new" element={<PermissionedRoute permission="customers.create"><CustomerFormPage /></PermissionedRoute>} />
                        <Route path="customers/:id/edit" element={<PermissionedRoute permission="customers.update"><CustomerFormPage /></PermissionedRoute>} />
                        <Route path="customers/:id/payment" element={<PermissionedRoute permission="customers.payments"><CustomerPaymentFormPage /></PermissionedRoute>} />
                        <Route path="customers/:id" element={<PermissionedRoute permission="customers"><CustomerDetailPage /></PermissionedRoute>} />
                        <Route path="customers" element={<PermissionedRoute permission="customers"><CustomersPage /></PermissionedRoute>} />
                        <Route path="sales/quotations/new" element={<PermissionedRoute permission="sales.invoices.create"><SalesQuotationFormPage /></PermissionedRoute>} />
                        <Route path="sales/quotations/:id/edit" element={<PermissionedRoute permission="sales.invoices.create"><SalesQuotationFormPage /></PermissionedRoute>} />
                        <Route path="sales/quotations" element={<PermissionedRoute permission="sales.invoices"><SalesQuotationsPage /></PermissionedRoute>} />
                        <Route path="sales/returns/new" element={<PermissionedRoute permission="sales.invoices.create"><SalesReturnFormPage /></PermissionedRoute>} />
                        <Route path="sales/returns" element={<PermissionedRoute permission="sales.invoices"><SalesReturnsPage /></PermissionedRoute>} />
                        <Route path="sales/invoices/new" element={<PermissionedRoute permission="sales.invoices.create"><SalesInvoiceFormPage /></PermissionedRoute>} />
                        <Route path="sales/invoices" element={<PermissionedRoute permission="sales.invoices"><SalesInvoicesPage /></PermissionedRoute>} />
                        <Route path="sales/receipts/new" element={<PermissionedRoute permission="sales.receipts.create"><SalesReceiptFormPage /></PermissionedRoute>} />
                        <Route path="sales/receipts/:id/edit" element={<PermissionedRoute permission="sales.receipts.create"><SalesReceiptFormPage /></PermissionedRoute>} />
                        <Route path="sales/receipts/:id" element={<PermissionedRoute permission="sales.receipts"><SalesReceiptDetailPage /></PermissionedRoute>} />
                        <Route path="sales/receipts" element={<PermissionedRoute permission="sales.receipts"><SalesReceiptsPage /></PermissionedRoute>} />
                        <Route path="sales/:id" element={<PermissionedRoute permission="sales"><SaleDetailPage /></PermissionedRoute>} />
                        <Route path="sales" element={<PermissionedRoute permission="sales"><SalesPage /></PermissionedRoute>} />
                        <Route path="bank/accounts/new" element={<PermissionedRoute permission="bank.create"><BankAccountFormPage /></PermissionedRoute>} />
                        <Route path="bank/accounts/:id/edit" element={<PermissionedRoute permissions={['bank.update', 'bank.create']}><BankAccountFormPage /></PermissionedRoute>} />
                        <Route path="bank/accounts" element={<PermissionedRoute permission="bank"><BankAccountsPage /></PermissionedRoute>} />
                        <Route path="bank/payments/new" element={<PermissionedRoute permission="bank.payments"><BankPaymentFormPage /></PermissionedRoute>} />
                        <Route path="bank/payments" element={<PermissionedRoute permission="bank.payments"><BankPaymentsPage /></PermissionedRoute>} />
                        <Route path="bank/receipts/new" element={<PermissionedRoute permission="bank.receipts"><BankReceiptFormPage /></PermissionedRoute>} />
                        <Route path="bank/receipts" element={<PermissionedRoute permission="bank.receipts"><BankReceiptsPage /></PermissionedRoute>} />
                        <Route path="bank/transfers/new" element={<PermissionedRoute permission="bank.transfers"><BankTransferFormPage /></PermissionedRoute>} />
                        <Route path="bank/transfers" element={<PermissionedRoute permission="bank.transfers"><BankTransfersPage /></PermissionedRoute>} />
                        <Route path="purchases/bills/new" element={<PermissionedRoute permission="purchases.create"><PurchaseBillFormPage /></PermissionedRoute>} />
                        <Route path="purchases/returns/new" element={<PermissionedRoute permission="purchases.create"><PurchaseReturnFormPage /></PermissionedRoute>} />
                        <Route path="purchases/returns/:id/edit" element={<PermissionedRoute permissions={['purchases.update', 'purchases.create']}><PurchaseReturnFormPage /></PermissionedRoute>} />
                        <Route path="purchases/returns/:id" element={<PermissionedRoute permission="purchases"><PurchaseReturnDetailPage /></PermissionedRoute>} />
                        <Route path="purchases/returns" element={<PermissionedRoute permission="purchases"><PurchaseReturnsPage /></PermissionedRoute>} />
                        <Route path="purchases/bills" element={<PermissionedRoute permission="purchases"><PurchaseBillsPage /></PermissionedRoute>} />
                        <Route path="purchases/payments/new" element={<PermissionedRoute permission="purchases.create"><PurchasePaymentFormPage /></PermissionedRoute>} />
                        <Route path="purchases/payments" element={<PermissionedRoute permission="purchases"><PurchasePaymentsPage /></PermissionedRoute>} />
                        <Route path="purchases/suppliers/new" element={<PermissionedRoute permission="purchases.create"><SupplierFormPage /></PermissionedRoute>} />
                        <Route path="purchases/suppliers/:id/edit" element={<PermissionedRoute permissions={['purchases.update', 'purchases.create']}><SupplierFormPage /></PermissionedRoute>} />
                        <Route path="purchases/suppliers/:id" element={<PermissionedRoute permission="purchases"><SupplierDetailPage /></PermissionedRoute>} />
                        <Route path="purchases/suppliers" element={<PermissionedRoute permission="purchases"><SuppliersPage /></PermissionedRoute>} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route path="reports" element={<PermissionedRoute permission="reports"><ReportsPage /></PermissionedRoute>} />
                        <Route path="users/new" element={<PermissionedRoute permission="users.create"><UserFormPage /></PermissionedRoute>} />
                        <Route path="users/:id/edit" element={<PermissionedRoute permission="users.update"><UserFormPage /></PermissionedRoute>} />
                        <Route path="users" element={<PermissionedRoute permission="users"><UsersPage /></PermissionedRoute>} />
                        <Route path="settings" element={<PermissionedRoute permission="settings"><SettingsPage /></PermissionedRoute>} />
                        <Route path="super-admin/new" element={<SuperAdminRoute><TenantFormPage /></SuperAdminRoute>} />
                        <Route path="super-admin/:id/edit" element={<SuperAdminRoute><TenantFormPage /></SuperAdminRoute>} />
                        <Route path="super-admin" element={<SuperAdminRoute><SuperAdminPage /></SuperAdminRoute>} />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Router>
        </AuthContext.Provider>
    );
}

export default App;
